import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ISlotBookingConfig } from '../../models/slot-booking.model';

@Component({
  standalone: false,
  selector: 'irembogov-slot-booking',
  templateUrl: './irembogov-slot-booking.component.html',
  styleUrls: ['./irembogov-slot-booking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IrembogovSlotBookingComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: IrembogovSlotBookingComponent,
      multi: true,
    },
  ],
})
export class IrembogovSlotBookingComponent
  implements OnInit, Validator, ControlValueAccessor, OnChanges
{
  @Input() configs: ISlotBookingConfig[] = [];
  @Input() customClass: string | undefined;
  @Input() loading: Record<string, boolean> = {};
  @Input()
  set items(
    value: Record<string, Record<string, unknown>[] | null> | undefined
  ) {
    this._items = value;
    if (value && !this.processingDefaults) {
      this.initializeDefaultValues();
    }
    this.handleAutoSelectSingle();
  }
  get items(): Record<string, Record<string, unknown>[] | null> | undefined {
    return this._items;
  }
  _items: Record<string, Record<string, unknown>[] | null> | undefined;
  form: FormGroup | undefined;
  @Output() activeDropDown = new EventEmitter<ISlotBookingConfig>();
  @Input() isTouched = false;
  @Input() remainingSeats: number | undefined = 0;
  @Input() invalidSlot = false;
  @Output() numberOfSeatsChange = new EventEmitter<any>();
  @Output() fetchMore = new EventEmitter<any>();
  @Output() rfaEvent = new EventEmitter<Record<string, unknown>>();
  @Input() appendTo = 'body';

  private normalizedConfigs: ISlotBookingConfig[] = [];
  private processingDefaults = false;

  constructor(private cd: ChangeDetectorRef, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.normalizeConfigs();
    this.form = this.fb.group(this.buildForm());
    if (this.items && Object.keys(this.items).length > 0) {
      this.initializeDefaultValues();
    } else {
      this.activeDropDown.emit({
        ...this.normalizedConfigs[0],
        value: undefined,
        index: 0,
      });
    }
    this.handleAutoSelectSingle();
  }

  normalizeConfigs(): void {
    const configsCopy = this.configs.map(config => ({ ...config }));

    const groupTypeConfig = configsCopy.find(c => !c.parent);
    const groupConfig = configsCopy.find(
      c => c.parent === groupTypeConfig?.key
    );
    const timeRangeConfig = configsCopy.find(
      c => c.parent === groupConfig?.key
    );
    const seatsConfig = configsCopy.find(
      c => c.isSeats || c.parent === timeRangeConfig?.key
    );

    if (!groupTypeConfig || !groupConfig || !timeRangeConfig || !seatsConfig) {
      console.warn('Missing required configuration fields');
      return;
    }

    const cleanConfig = (config: ISlotBookingConfig): ISlotBookingConfig => {
      const cleaned: ISlotBookingConfig = {
        key: config.key,
        label: config.label,
        required: config.required ?? true,
        bindlabel: config.bindlabel ?? 'name',
        bindvalue: config.bindvalue ?? 'id',
        placeholder: config.placeholder ?? '',
        requiredErrorMessage: config.requiredErrorMessage,
        defaultValue: config.defaultValue ?? null,
        hidden: config.hidden ?? false,
        maxSeats: config.maxSeats,
        autoSelectSingle: config.autoSelectSingle ?? true,
      };

      if (config.maxSeatErrorMessage)
        cleaned.maxSeatErrorMessage = config.maxSeatErrorMessage;
      if (config.minSeatErrorMessage)
        cleaned.minSeatErrorMessage = config.minSeatErrorMessage;
      if (config.numbersOnlyErrorMessage)
        cleaned.numbersOnlyErrorMessage = config.numbersOnlyErrorMessage;

      return cleaned;
    };

    this.normalizedConfigs = [
      {
        ...cleanConfig(groupTypeConfig),
        type: 'customdropdown',
      },
      {
        ...cleanConfig(groupConfig),
        type: 'customdropdown',
        parent: groupTypeConfig.key,
      },
      {
        ...cleanConfig(timeRangeConfig),
        type: 'customdropdown',
        parent: groupConfig.key,
      },
      {
        ...cleanConfig(seatsConfig),
        type: 'number',
        parent: timeRangeConfig.key,
        isSeats: true,
      },
    ];

    this.configs = this.normalizedConfigs;
  }

  async initializeDefaultValues(): Promise<void> {
    if (!this.form || !this.items || this.processingDefaults) return;

    try {
      this.processingDefaults = true;

      for (let i = 0; i < this.normalizedConfigs.length; i++) {
        const config = this.normalizedConfigs[i];
        const defaultValue = config.defaultValue;

        if (defaultValue !== undefined && defaultValue !== null) {
          const items = this.items[config.key];

          if (config.type === 'customdropdown' && Array.isArray(items)) {
            const defaultItem = items.find(item => item['id'] === defaultValue);

            if (defaultItem) {
              this.form.get(config.key)?.setValue(defaultItem);
              await this.waitForNextTick();
              this.onSelectChange(defaultItem, config.key, i);
            }
          } else if (config.type === 'number') {
            this.form.get(config.key)?.setValue(defaultValue);
            this.numberOfSeatsChange.emit({ target: { value: defaultValue } });
          }
        }
      }

      this._onChange(this.form.value);
    } finally {
      this.processingDefaults = false;
    }
  }

  async waitForNextTick(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  resetDropDowns(key: string, startIndex: number) {
    if (this.processingDefaults) return;

    this.configs.slice(startIndex).forEach(child => {
      this.form?.controls[child.key].setValue(undefined);
    });
  }

  areItemsLoaded(key: string): boolean {
    return !!(this.items && this.items[key] && Array.isArray(this.items[key]));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['isTouched'] && this.form?.invalid) {
      this.form.markAllAsTouched();
    }

    if (changes?.['items']?.currentValue && !this.processingDefaults) {
      this.initializeDefaultValues();
    }

    if (changes?.['remainingSeats']) {
      const seatsConfig = this.normalizedConfigs.find(config => config.isSeats);
      if (seatsConfig) {
        const maxAllowedSeats = seatsConfig.maxSeats
          ? Math.min(seatsConfig.maxSeats, this.remainingSeats ?? 0)
          : this.remainingSeats;

        const seatControl = this.form?.controls[seatsConfig.key];
        if (seatControl) {
          seatControl.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.min(maxAllowedSeats ?? 0),
            Validators.pattern(/^[0-9]+$/),
          ]);
          seatControl.updateValueAndValidity();
        }
      }
      this.cd.detectChanges();
    }

    if (changes?.['invalidSlot']) {
      this._onValidationChange();
    }
  }

  onSelectChange(value: Record<string, unknown>, key: string, index: number) {
    if (this.processingDefaults && !value) return;

    const nextItemIndex = index + 1;
    this.resetDropDowns(key, nextItemIndex);

    const formValue = this.form?.value;
    if (!this.processingDefaults && formValue) {
      this._onChange(formValue);
      this._onTouch(formValue);
    }

    if (this.configs.length - 1 === index || !value) return;

    this.activeDropDown.emit({
      ...this.configs[nextItemIndex],
      value,
      index: nextItemIndex,
    });
  }

  onKeyUp(event: any) {
    this._onChange(this.form?.value);
    this._onTouch(this.form?.value);
    this.numberOfSeatsChange.emit(event);
    this.cd.detectChanges();
  }

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange = (value: unknown) => {};
  private _onTouch = (value: unknown) => {};
  private _onValidationChange = () => {};
  /* eslint-enable */

  writeValue(obj: { [key: string]: any }): void {
    if (obj) {
      this.form?.setValue(obj);
      this.form?.markAllAsTouched();
      this.form?.updateValueAndValidity();
      this.cd.detectChanges();
      this.rfaEvent.emit(obj);
    }
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: (_: unknown) => void): void {
    this._onTouch = fn;
  }

  validate(): ValidationErrors | null {
    const validationErrors: ValidationErrors = {};

    if (this.invalidSlot) {
      validationErrors['invalidSlot'] = true;
    }

    if (this.form?.invalid) {
      validationErrors['invalid'] = true;
    }

    return Object.keys(validationErrors).length ? validationErrors : null;
  }

  buildForm(): Record<string, FormControl> {
    const group: Record<string, FormControl> = {};
    this.normalizedConfigs.forEach(field => {
      if (field.isSeats) {
        const seatsConfig = this.normalizedConfigs.find(
          config => config.isSeats
        );
        const maxAllowedSeats = seatsConfig?.maxSeats
          ? Math.min(seatsConfig.maxSeats, this.remainingSeats ?? 0)
          : this.remainingSeats;

        group[field.key] = new FormControl(field.defaultValue ?? null, [
          Validators.required,
          Validators.min(1),
          Validators.min(maxAllowedSeats ?? 0),
          Validators.pattern(/^[0-9]+$/),
        ]);
      } else {
        group[field.key] = new FormControl(
          field.defaultValue ?? null,
          Validators.required
        );
      }
    });
    return group;
  }

  handlePagination(_event: any, index: number) {
    this.fetchMore.emit({ index });
  }

  hasItems(field: ISlotBookingConfig): boolean {
    const items = this.items?.[field.key];
    return (
      (Array.isArray(items) && items.length > 0) || field.type === 'number'
    );
  }

  handleAutoSelectSingle(): void {
    if (!this.items || !this.form) return;

    for (const [index, config] of this.normalizedConfigs.entries()) {
      if (config.type === 'customdropdown' && config.autoSelectSingle) {
        const items = this.items?.[config.key];
        if (
          Array.isArray(items) &&
          items.length === 1 &&
          !this.form.get(config.key)?.value
        ) {
          this.form?.get(config.key)?.setValue(items[0], { emitEvent: false });
          this.onSelectChange(items[0], config.key, index);
          break;
        }
      }
    }
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidationChange = fn;
  }
}
